import { createSelector } from "reselect";

import { ScanLabelArchetype } from "constants/scanLabels";

import { EMPTY_ARRAY } from "lib";

import {
  createAggregateIdsByKeySelector,
  createLookupCombiner,
  createLookupSelectors,
  objectMapper,
} from "./lib";
import { getScans } from "./root";
import { getScanLabels } from "./root.state";
import { selectScansBySaleLotIdLookup } from "./scanners";

export const selectArchetypeByScanLabelName: (
  state: State,
) => Record<string, ScanLabelArchetype> = createSelector(
  [getScanLabels],
  (scanLabelsById: Record<string, ScanLabel>) => {
    const archetypeByName: Record<string, ScanLabelArchetype> = {};

    Object.values(ScanLabelArchetype)
      .filter(archetype => archetype !== ScanLabelArchetype.NONE)
      .forEach(archetype => {
        archetypeByName[archetype] = archetype;
      });

    Object.values(scanLabelsById).forEach(scanLabel => {
      if (!(scanLabel.name in archetypeByName)) {
        archetypeByName[scanLabel.name] = scanLabel.archetype;
      }
    });

    return archetypeByName;
  },
);

export const getScanLabelById =
  (scanLabelId: string | null) => (state: State) =>
    getScanLabels(state)[scanLabelId] || null;

export const selectScanLabelsAggridData = createSelector(
  [getScanLabels],
  (scanLabelsByIdLookup: Record<string, ScanLabel>) =>
    Object.values(scanLabelsByIdLookup),
);

export const selectScanLabelIdsByScanIdLookup: (
  state: State,
) => Record<string, string[]> = createAggregateIdsByKeySelector(
  getScanLabels,
  "scanId",
);

export const getScanLabelIdsByScanId = (scanId: string) => (state: State) =>
  selectScanLabelIdsByScanIdLookup(state)[scanId] || EMPTY_ARRAY;

export const [selectScanLabelsByScanIdLookup, getScanLabelsByScanId] =
  createLookupSelectors(
    [selectScanLabelIdsByScanIdLookup, getScanLabels],
    createLookupCombiner(objectMapper),
    EMPTY_ARRAY,
  ) as [
    (state: State) => Record<string, ScanLabel[]>,
    (scanId: string) => (state: State) => ScanLabel[],
  ];

// `getScans` is keyed by EID, whereas ScanLabel.scanId refers to the scan's
// own id - so this lookup joins the two by scan.id to key by EID instead.
export const [selectScanLabelIdsByEIDLookup, getScanLabelIdsByEID] =
  createLookupSelectors(
    [getScans, selectScanLabelIdsByScanIdLookup],
    createLookupCombiner(
      (scan: Scan, idsByScanIdLookup: Record<string, string[]>) =>
        idsByScanIdLookup[scan.id] || EMPTY_ARRAY,
    ),
  );

export const [selectScanLabelsByEIDLookup, getScanLabelsByEID] =
  createLookupSelectors(
    [selectScanLabelIdsByEIDLookup, getScanLabels],
    createLookupCombiner(objectMapper),
    EMPTY_ARRAY,
  ) as [
    (state: State) => Record<string, ScanLabel[]>,
    (eid: string) => (state: State) => ScanLabel[],
  ];

export const [selectScanLabelIdsBySaleLotIdLookup, getScanLabelIdsBySaleLotId] =
  createLookupSelectors(
    [selectScansBySaleLotIdLookup, selectScanLabelIdsByScanIdLookup],
    createLookupCombiner(
      (scans: Scan[], idsByScanIdLookup: Record<string, string[]>) =>
        scans.flatMap((scan: Scan) => idsByScanIdLookup[scan.id] || []),
    ),
  );

export const [selectScanLabelsBySaleLotIdLookup, getScanLabelsBySaleLotId] =
  createLookupSelectors(
    [selectScanLabelIdsBySaleLotIdLookup, getScanLabels],
    createLookupCombiner(objectMapper),
    EMPTY_ARRAY,
  ) as [
    (state: State) => Record<string, ScanLabel[]>,
    (saleLotId: string) => (state: State) => ScanLabel[],
  ];

function countScanLabelsByArchetype(
  labels: ScanLabel[],
): Record<ScanLabelArchetype, number> {
  const counts: Record<ScanLabelArchetype, number> = {
    [ScanLabelArchetype.NONE]: 0,
    [ScanLabelArchetype.DECEASED]: 0,
    [ScanLabelArchetype.NON_READER]: 0,
    [ScanLabelArchetype.NO_TAG]: 0,
  };
  // Dedupe by (scanId, archetype) so a scan with two labels of the
  // same archetype only contributes once to that archetype's count.
  const seenScanArchetypes = new Set<string>();
  labels.forEach(label => {
    const key = `${label.scanId}:${label.archetype}`;
    if (!seenScanArchetypes.has(key)) {
      seenScanArchetypes.add(key);
      counts[label.archetype] += 1;
    }
  });
  return counts;
}

export const [
  selectScanLabelArchetypeCountsBySaleLotIdLookup,
  getScanLabelArchetypeCountsBySaleLotId,
] = createLookupSelectors(
  [selectScanLabelsBySaleLotIdLookup],
  createLookupCombiner(countScanLabelsByArchetype),
);
